import { useParams, Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import { categories } from '@/mocks/categories';
import { games, type Game } from '@/mocks/games';

const categoryNameMap: Record<string, string> = {
  all: '全部游戏',
  casual: '休闲益智',
  rpg: '角色扮演',
  strategy: '策略竞技',
  board: '棋牌游戏',
  sports: '体育竞速',
};

const categoryColorMap: Record<string, string> = {
  casual: 'bg-green-100 text-green-600',
  rpg: 'bg-red-100 text-red-600',
  strategy: 'bg-amber-100 text-amber-600',
  board: 'bg-teal-100 text-teal-600',
  sports: 'bg-orange-100 text-orange-600',
};

const gameThumbnails: Record<string, string> = {
  'ng-lucky-wheel': 'https://readdy.ai/api/search-image?query=Golden%20lucky%20wheel%20game%20thumbnail%2C%20dark%20elegant%20background%2C%20emerald%20green%20and%20gold%20accents%2C%20casino%20style%2C%20minimalist&width=400&height=300&seq=thumb-lucky-wheel&orientation=landscape',
  'ng-legend-sword': 'https://readdy.ai/api/search-image?query=Fantasy%20sword%20RPG%20game%20thumbnail%2C%20dark%20epic%20background%2C%20emerald%20green%20magic%20glow%2C%20gold%20accents%2C%20cinematic&width=400&height=300&seq=thumb-legend-sword&orientation=landscape',
  'ng-chess-empire': 'https://readdy.ai/api/search-image?query=Card%20game%20thumbnail%20with%20playing%20cards%2C%20dark%20green%20felt%20background%2C%20gold%20coins%2C%20luxurious%20gaming%20club&width=400&height=300&seq=thumb-chess-empire&orientation=landscape',
  'ng-strategy-kingdom': 'https://readdy.ai/api/search-image?query=Strategy%20kingdom%20game%20thumbnail%2C%20dark%20epic%20background%2C%20gold%20crown%20and%20emerald%20banners%2C%20ancient%20war%20style&width=400&height=300&seq=thumb-strategy-kingdom&orientation=landscape',
  'ng-speed-racer': 'https://readdy.ai/api/search-image?query=Racing%20car%20game%20thumbnail%2C%20dark%20night%20city%20background%2C%20neon%20green%20and%20gold%20lights%2C%20speed%20motion&width=400&height=300&seq=thumb-speed-racer&orientation=landscape',
  'ng-fish-joy': 'https://readdy.ai/api/search-image?query=Underwater%20fish%20game%20thumbnail%2C%20dark%20ocean%20background%2C%20emerald%20green%20light%20rays%2C%20colorful%20fish%2C%20casual&width=400&height=300&seq=thumb-fish-joy&orientation=landscape',
  'ng-dark-dungeon': 'https://readdy.ai/api/search-image?query=Dark%20dungeon%20game%20thumbnail%2C%20torch%20light%20and%20ruins%2C%20emerald%20green%20glow%2C%20gold%20treasure%2C%20mysterious%20fantasy&width=400&height=300&seq=thumb-dark-dungeon&orientation=landscape',
  'ng-poker-tournament': 'https://readdy.ai/api/search-image?query=Poker%20game%20thumbnail%2C%20dark%20casino%20background%2C%20emerald%20green%20table%2C%20gold%20chips%2C%20luxurious&width=400&height=300&seq=thumb-poker-tournament&orientation=landscape',
  'ng-bubble-shooter': 'https://readdy.ai/api/search-image?query=Bubble%20shooter%20game%20thumbnail%2C%20dark%20background%2C%20colorful%20bubbles%2C%20emerald%20green%20accents%2C%20casual%20cute&width=400&height=300&seq=thumb-bubble-shooter&orientation=landscape',
  'ng-tower-defense': 'https://readdy.ai/api/search-image?query=Tower%20defense%20game%20thumbnail%2C%20dark%20battlefield%20background%2C%20emerald%20green%20towers%2C%20gold%20effects%2C%20strategy&width=400&height=300&seq=thumb-tower-defense&orientation=landscape',
  'ng-basketball-star': 'https://readdy.ai/api/search-image?query=Basketball%20game%20thumbnail%2C%20dark%20court%20background%2C%20neon%20green%20and%20gold%20lights%2C%20sports%20action&width=400&height=300&seq=thumb-basketball-star&orientation=landscape',
  'ng-mahjong-master': 'https://readdy.ai/api/search-image?query=Mahjong%20game%20thumbnail%2C%20dark%20green%20table%20background%2C%20gold%20tiles%20and%20accents%2C%20Chinese%20traditional%20style&width=400&height=300&seq=thumb-mahjong-master&orientation=landscape',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState<'popular' | 'rating' | 'size'>('popular');

  const activeCategory = categories.find((c) => c.slug === slug) || categories[0];
  const filteredGames: Game[] = slug === 'all'
    ? games
    : games.filter((g) => g.category === slug);

  const sortedGames = [...filteredGames].sort((a, b) => {
    if (sortBy === 'rating') return b.rating - a.rating;
    if (sortBy === 'size') return parseInt(a.size) - parseInt(b.size);
    const parseDownloads = (d: string) => parseInt(d.replace(/[万+]/g, '')) * 10000;
    return parseDownloads(b.downloads) - parseDownloads(a.downloads);
  });

  const handleTabChange = (newSlug: string) => {
    navigate(`/category/${newSlug}`);
  };

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: '游戏分类', path: '/category/all' },
    { label: activeCategory.name },
  ];

  const categoryWebpageSchema = {
    name: `NG南宫 - ${activeCategory.name}`,
    description: activeCategory.description,
    url: `https://ngnangong.com/category/${slug}`,
  };

  const breadcrumbSchema = {
    itemListElement: breadcrumbItems.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.label,
      item: item.path ? `https://ngnangong.com${item.path}` : undefined,
    })),
  };

  return (
    <>
      <SchemaOrg type="WebPage" data={categoryWebpageSchema} />
      <SchemaOrg type="BreadcrumbList" data={breadcrumbSchema} />

      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100 border-b border-secondary-200/60">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />
            <div className="pb-6">
              <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-2">
                {activeCategory.name}
              </h1>
              <p className="text-sm text-foreground-500">{activeCategory.description}</p>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            <aside className="lg:w-56 flex-shrink-0">
              <nav className="flex lg:flex-col gap-1.5 overflow-x-auto pb-2 lg:pb-0" aria-label="游戏分类导航">
                {categories.map((cat) => (
                  <button
                    key={cat.slug}
                    onClick={() => handleTabChange(cat.slug)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap cursor-pointer transition-colors duration-200 ${
                      activeCategory.slug === cat.slug
                        ? 'bg-primary-100 text-primary-600'
                        : 'text-foreground-500 hover:bg-background-200/70'
                    }`}
                  >
                    <i className={`${cat.icon} text-sm`}></i>
                    <span>{cat.name}</span>
                    <span className="text-foreground-300 ml-auto">({cat.count})</span>
                  </button>
                ))}
              </nav>
            </aside>

            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-5">
                <p className="text-xs text-foreground-500">
                  共 <strong className="text-foreground-800">{filteredGames.length}</strong> 款游戏
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-foreground-400">排序：</span>
                  {(['popular', 'rating', 'size'] as const).map((s) => (
                    <button
                      key={s}
                      onClick={() => setSortBy(s)}
                      className={`px-3 py-1 rounded-full text-xs font-medium cursor-pointer transition-colors duration-200 whitespace-nowrap ${
                        sortBy === s
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-200/70 text-foreground-500 hover:bg-background-300/70'
                      }`}
                    >
                      {s === 'popular' ? '最热' : s === 'rating' ? '评分' : '大小'}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-product-shop="true">
                {sortedGames.map((game) => (
                  <article
                    key={game.id}
                    className="group flex gap-4 p-4 rounded-lg bg-background-100 hover:bg-background-200/70 transition-colors duration-200 cursor-pointer"
                  >
                    <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden">
                      <img
                        src={gameThumbnails[game.id] || gameThumbnails['ng-lucky-wheel']}
                        alt={game.name}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-heading text-sm text-foreground-900 truncate">{game.name}</h3>
                        <span className={`px-1.5 py-0.5 rounded text-xs font-medium flex-shrink-0 ${categoryColorMap[game.category] || 'bg-gray-100'}`}>
                          {categoryNameMap[game.category]}
                        </span>
                      </div>
                      <p className="text-xs text-foreground-400 mt-1">{game.size} · v{game.version}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="flex items-center gap-0.5 text-xs text-accent-500">
                          <i className="ri-star-fill text-xs"></i> {game.rating}
                        </span>
                        <span className="text-xs text-foreground-400">下载 {game.downloads}</span>
                      </div>
                      <div className="flex items-center gap-1 mt-2">
                        {game.platforms.includes('iOS') && (
                          <span className="w-5 h-5 flex items-center justify-center rounded bg-foreground-100 text-foreground-500 text-xs">
                            <i className="ri-apple-fill"></i>
                          </span>
                        )}
                        {game.platforms.includes('Android') && (
                          <span className="w-5 h-5 flex items-center justify-center rounded bg-foreground-100 text-foreground-500 text-xs">
                            <i className="ri-android-fill"></i>
                          </span>
                        )}
                        {game.platforms.includes('PC') && (
                          <span className="w-5 h-5 flex items-center justify-center rounded bg-foreground-100 text-foreground-500 text-xs">
                            <i className="ri-computer-line"></i>
                          </span>
                        )}
                      </div>
                    </div>
                  </article>
                ))}
              </div>

              {filteredGames.length === 0 && (
                <div className="text-center py-16">
                  <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200/70 text-foreground-300 mb-4">
                    <i className="ri-gamepad-line text-2xl"></i>
                  </div>
                  <p className="text-sm text-foreground-500">该分类暂无游戏</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}